import Phaser from 'phaser';
import {
  BaseEntityDTO,
  Battlefield,
  BattlefieldDefDTO,
  CombatPosition,
  CombatStateDTO,
  EntityCombatStateDTO,
  EntityStatus,
  EntityType,
  GameStateDTO,
  hashDefinition,
} from '@hexcrawl/shared-utils';
import { enemyService } from '@/ui/services/enemyService';
import { npcService } from '@/ui/services/npcService';
import { useNpcStore } from '@/ui/stores/npcStore';
import { combatService } from '@/ui/services/combatService';
import { abilityService } from '@/ui/services/abilityService';
import { abilityDisplayEvents } from '@/ui/services/abilityDisplayEvents';
import { useCombatStore } from '@/ui/stores/combatStore';
import { spendInventoryUnit, useGameStore } from '@/ui/stores/gameStore';
import { getSpriteAssetKey, getSpriteAssetType, getSpriteUrl } from '@/utils/sprites.ts';
import { loadCharacterSprite } from '@/sprites/phaserSpriteLoader.ts';
import { runPredictiveAction } from '@/utils/predictiveAction';
import type { EnqueueOptions } from '@/ui/services/turnEventQueue';
import Tilemap = Phaser.Tilemaps.Tilemap;
import type { CombatActionResponseDTO } from '@hexcrawl/shared-utils';

import { CombatCoordinates } from '@/combat/CombatCoordinates';
import { CombatAbilityDisplay } from '@/combat/CombatAbilityDisplay';
import { CombatHighlights } from '@/combat/CombatHighlights';
import { CombatTurnProcessor } from '@/combat/CombatTurnProcessor';
import { CombatContext } from '@/combat/CombatContext';
import { CombatEntityManager } from '@/combat/CombatEntityManager.ts';
import { createCombatMoveTween } from '@/combat/combatMovement';
import { COMBAT_ACTION_BAR_HEIGHT, DepthOrder, SPRITE_LAYER_OVERLAP_ALPHA } from '@/combat/constants';
import type { HydratedRoster } from '@/combat/constants';

export class CombatScene extends Phaser.Scene {
  private gameState!: GameStateDTO;
  private _battlefieldDef!: BattlefieldDefDTO;
  private _tilemap!: Tilemap;
  private _upperTileSprites: Phaser.GameObjects.Image[] = [];
  private _abilityDisplayUnsubscribe: (() => void) | null = null;
  private _windowPointerMoveHandler: ((event: PointerEvent) => void) | null = null;
  private _windowPointerDownHandler: ((event: PointerEvent) => void) | null = null;
  private _windowPointerUpHandler: ((event: PointerEvent) => void) | null = null;
  private _wheelHandler: ((event: WheelEvent) => void) | null = null;

  // Zoom & pan state
  private _minZoom = 1;
  private static readonly MAX_ZOOM_MULTIPLIER = 3;
  private static readonly DRAG_THRESHOLD = 5;
  private static readonly ZOOM_FACTOR = 0.03;
  private static readonly CAMERA_STATE_KEY = 'combat_camera';
  private _isDragging = false;
  private _isLeftDown = false;
  private _dragStartX = 0;
  private _dragStartY = 0;
  private _cameraStartScrollX = 0;
  private _cameraStartScrollY = 0;
  private _isFirstLayout = true;
  private _activePointerPositions = new Map<number, { x: number; y: number }>();
  private _pinchActive = false;
  private _pinchLastDistance = 0;

  // Sub-modules
  private _coords!: CombatCoordinates;
  private _abilityDisplay!: CombatAbilityDisplay;
  private _highlights!: CombatHighlights;
  private _turnProcessor!: CombatTurnProcessor;
  private _context!: CombatContext;
  private _entityManager!: CombatEntityManager;

  constructor() {
    super({ key: 'CombatScene' });
  }

  init(data: { gameState: GameStateDTO; battlefieldDef: BattlefieldDefDTO }) {
    this.gameState = data.gameState;
    this._battlefieldDef = data.battlefieldDef;
  }

  preload() {
    const assets = this._battlefieldDef.clientAssets;

    this.load.atlas('projectiles', 'assets/sprites/projectiles.png', 'assets/sprites/projectiles.json');
    this.load.json('particles', 'assets/particles.json');

    // Equipment icon atlases back the Use Item popover. Normally MapScene has already
    // loaded them, but resuming a saved game mid-combat starts here without it.
    const atlasNames = useGameStore.getState().gameConfig?.equipmentAtlases ?? [];
    for (const atlas of atlasNames) {
      if (!this.textures.exists(atlas)) {
        this.load.atlas(atlas, `/assets/images/${atlas}.png`, `/assets/images/${atlas}.json`);
      }
    }

    // Phaser skips loading a key that already exists in cache. Evict the tilemap so a
    // different battlefield map is fetched fresh on subsequent combats. Tilesets and images
    // are shared across all battlefields and can remain cached.
    this.cache.tilemap.remove('battlefield-map');

    this.load.tilemapTiledJSON('battlefield-map', assets.mapUrl);
    for (const ts of assets.tilesets) {
      this.load.json(`tileset-${ts.key}`, ts.url);
    }
    for (const img of assets.images) {
      this.load.image(img.key, img.url);
    }
  }

  async create() {
    // Reset sub-modules so the update() guard (if (!this._turnProcessor) return)
    // prevents syncFromStore from running against stale/destroyed sprites while
    // we await async setup below.
    this._turnProcessor = null!;
    this._isFirstLayout = true;

    this.cameras.main.setBackgroundColor('#101018');

    this.scale.on('resize', () => {
      this.layoutScene();
    });

    try {
      await this.loadParticleTextures();
      const [combat, battlefield] = await this.loadCombat();
      const roster = await this.hydrateRoster(combat);
      await this.loadRosterSprites(roster);
      this.createSceneObjects(combat, battlefield, roster);
      this.setupInput();
      this.setupAbilityDisplayEvents();
      this.registerEnqueueHandler();
      this.layoutScene();
    } catch (e) {
      console.error('[CombatScene] Failed to load combat', e);
      this.add.text(20, 20, 'Combat Failed to Load', { color: '#ff5555' });
    }
  }

  private loadParticleTextures(): Promise<void> {
    const data = this.cache.json.get('particles') as { emitters: Record<string, Record<string, unknown>> } | null;
    if (!data?.emitters) return Promise.resolve();

    const promises = Object.entries(data.emitters).map(([name, def]) => {
      if (this.textures.exists(name)) return Promise.resolve();
      const base64 = def[name];
      if (typeof base64 !== 'string') return Promise.resolve();
      return new Promise<void>((resolve) => {
        const onAdd = (key: string) => {
          if (key === name) {
            this.textures.off('addtexture', onAdd);
            resolve();
          }
        };
        this.textures.on('addtexture', onAdd);
        this.textures.addBase64(name, base64);
      });
    });

    return Promise.all(promises).then(() => {});
  }

  private initSubModules(combat: CombatStateDTO, battlefield: Battlefield, roster: HydratedRoster) {
    this._coords = new CombatCoordinates(this._tilemap);

    this._context = new CombatContext(combat, this.gameState, battlefield, roster, this._battlefieldDef);
    this._entityManager = new CombatEntityManager();
    this._entityManager.initEntities(this, this._context.combat, this._coords, this._context);

    this._abilityDisplay = new CombatAbilityDisplay(this, this._coords, this._entityManager, this._context);
    this._highlights = new CombatHighlights(this, this._coords, this._tilemap.tileWidth, this._tilemap.tileHeight, this._context);
    this._turnProcessor = new CombatTurnProcessor(
      this,
      this._coords,
      this._abilityDisplay,
      this._context,
      this._entityManager,
      () => {
        this._context.updateFromGameStore();
        this._entityManager.sync(this, this._coords, this._context);
        this._highlights.upateHighlights();
      },
    );
  }

  private registerEnqueueHandler() {
    const handler = async (response: CombatActionResponseDTO, options?: EnqueueOptions) => {
      await this._turnProcessor.enqueueAndProcessResponse(response, options);
    };
    useCombatStore.getState().setEnqueueResponse(handler);
    this.events.once('shutdown', () => {
      useCombatStore.getState().setEnqueueResponse(null);
      this.saveCameraState();
    });
  }

  private setupAbilityDisplayEvents() {
    if (this._abilityDisplayUnsubscribe) return;
    this._abilityDisplayUnsubscribe = abilityDisplayEvents.subscribe((payload) => {
      if (this._turnProcessor.queueActive) return;
      this._abilityDisplay.handleAbilityDisplayEvent(payload);
    });
    this.events.once('shutdown', () => {
      this._abilityDisplayUnsubscribe?.();
      this._abilityDisplayUnsubscribe = null;
    });
  }

  private getPinchDistance(): number {
    const pts = [...this._activePointerPositions.values()];
    if (pts.length < 2) return 0;
    const dx = pts[1].x - pts[0].x;
    const dy = pts[1].y - pts[0].y;
    return Math.sqrt(dx * dx + dy * dy);
  }

  private pageToCanvasCoords(event: PointerEvent): { x: number; y: number } {
    const canvas = this.sys.game.canvas;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return {
      x: (event.clientX - rect.left) * scaleX,
      y: (event.clientY - rect.top) * scaleY,
    };
  }

  private setupInput() {
    console.log('[CombatScene] Setting up input handlers');
    this.input.setPollAlways();

    if (!this._windowPointerDownHandler) {
      this._windowPointerDownHandler = (event: PointerEvent) => {
        const { x, y } = this.pageToCanvasCoords(event);
        this._activePointerPositions.set(event.pointerId, { x, y });

        if (this._activePointerPositions.size === 2) {
          this._pinchActive = true;
          this._pinchLastDistance = this.getPinchDistance();
          this._isLeftDown = false;
          this._isDragging = false;
          return;
        }

        if (event.button !== 0) return;  // left button only
        this._isLeftDown = true;
        this._isDragging = false;
        this._dragStartX = x;
        this._dragStartY = y;
        this._cameraStartScrollX = this.cameras.main.scrollX;
        this._cameraStartScrollY = this.cameras.main.scrollY;
      };
      window.addEventListener('pointerdown', this._windowPointerDownHandler);
      this.events.once('shutdown', () => {
        if (this._windowPointerDownHandler) {
          window.removeEventListener('pointerdown', this._windowPointerDownHandler);
          this._windowPointerDownHandler = null;
        }
      });
    }

    if (!this._windowPointerMoveHandler) {
      this._windowPointerMoveHandler = (event: PointerEvent) => {
        const { x: screenX, y: screenY } = this.pageToCanvasCoords(event);

        if (this._activePointerPositions.has(event.pointerId)) {
          this._activePointerPositions.set(event.pointerId, { x: screenX, y: screenY });
        }

        if (this._pinchActive) {
          const newDist = this.getPinchDistance();
          if (this._pinchLastDistance > 0) {
            const camera = this.cameras.main;
            const maxZoom = this._minZoom * CombatScene.MAX_ZOOM_MULTIPLIER;
            camera.setZoom(Phaser.Math.Clamp(camera.zoom * (newDist / this._pinchLastDistance), this._minZoom, maxZoom));
          }
          this._pinchLastDistance = newDist;
          return;
        }

        if (this._isLeftDown && !this._isDragging) {
          const dx = screenX - this._dragStartX;
          const dy = screenY - this._dragStartY;
          if (Math.abs(dx) > CombatScene.DRAG_THRESHOLD || Math.abs(dy) > CombatScene.DRAG_THRESHOLD) {
            this._isDragging = true;
          }
        }

        if (this._isDragging) {
          const camera = this.cameras.main;
          const dx = screenX - this._dragStartX;
          const dy = screenY - this._dragStartY;
          camera.setScroll(
            this._cameraStartScrollX - dx / camera.zoom,
            this._cameraStartScrollY - dy / camera.zoom,
          );
        } else {
          const worldPos = this.cameras.main.getWorldPoint(screenX, screenY);
          const tilePos = this._coords.worldToTile(worldPos.x, worldPos.y, true);
          this._highlights.updateHoverHighlight(tilePos, this._turnProcessor.queueActive);
        }
      };
      window.addEventListener('pointermove', this._windowPointerMoveHandler);
      this.events.once('shutdown', () => {
        if (this._windowPointerMoveHandler) {
          window.removeEventListener('pointermove', this._windowPointerMoveHandler);
          this._windowPointerMoveHandler = null;
        }
      });
    }

    if (!this._windowPointerUpHandler) {
      this._windowPointerUpHandler = (event: PointerEvent) => {
        this._activePointerPositions.delete(event.pointerId);

        if (this._pinchActive) {
          if (this._activePointerPositions.size < 2) {
            this._pinchActive = false;
            this._pinchLastDistance = 0;
            this.saveCameraState();
          }
          return;
        }

        if (event.button !== 0) return;
        if (this._isLeftDown && !this._isDragging) {
          // This was a click, not a drag
          if (!useCombatStore.getState().queueBusy) {
            const { x: screenX, y: screenY } = this.pageToCanvasCoords(event);
            const worldPos = this.cameras.main.getWorldPoint(screenX, screenY);
            const tilePos = this._coords.worldToTile(worldPos.x, worldPos.y, true);
            if (tilePos) {
              const combatStore = useCombatStore.getState();
              if (combatStore.inputMode === 'movement') {
                this.handleMovementClick(tilePos);
              } else if (combatStore.inputMode === 'target-selection') {
                this.handleTargetClick(tilePos);
              }
            }
          }
        }
        if (this._isDragging) {
          this.saveCameraState();
        }
        this._isLeftDown = false;
        this._isDragging = false;
      };
      window.addEventListener('pointerup', this._windowPointerUpHandler);
      this.events.once('shutdown', () => {
        if (this._windowPointerUpHandler) {
          window.removeEventListener('pointerup', this._windowPointerUpHandler);
          this._windowPointerUpHandler = null;
        }
      });
    }

    if (!this._wheelHandler) {
      this._wheelHandler = (event: WheelEvent) => {
        event.preventDefault();
        const camera = this.cameras.main;
        const maxZoom = this._minZoom * CombatScene.MAX_ZOOM_MULTIPLIER;
        const direction = event.deltaY > 0 ? -1 : 1;
        const newZoom = Phaser.Math.Clamp(
          camera.zoom * (1 + direction * CombatScene.ZOOM_FACTOR),
          this._minZoom,
          maxZoom,
        );
        camera.setZoom(newZoom);
        this.saveCameraState();
      };
      this.sys.game.canvas.addEventListener('wheel', this._wheelHandler, { passive: false });
      this.events.once('shutdown', () => {
        if (this._wheelHandler) {
          this.sys.game.canvas.removeEventListener('wheel', this._wheelHandler);
          this._wheelHandler = null;
        }
      });
    }
  }

  private async handleMovementClick(tilePos: CombatPosition) {
    if (!this._context.canPlayerMove()) return;
    if (!this._context.isValidMoveTarget(tilePos)) return;

    const currentEntity = this._context.getCurrentTurnEntity();
    if (!currentEntity) return;
    const sprite = this._entityManager.sprites.get(currentEntity.entityId);
    if (!sprite) return;

    useCombatStore.getState().setQueueBusy(true);
    this._highlights.upateHighlights();
    this._highlights.updateHoverHighlight(null, true);
    this._entityManager.addPredictiveMoveEntity(currentEntity.entityId);
    try {
      const startWorld = { x: sprite.x, y: sprite.y };
      let moveTween: Phaser.Tweens.Tween | null = null;
      const result = await runPredictiveAction({
        startAnimation: () => new Promise<void>((resolve) => {
          sprite.playAnimation('walk');
          moveTween = createCombatMoveTween(
            this,
            sprite,
            this._coords,
            this._context.battlefield,
            currentEntity.position,
            tilePos,
            { ignoreEntityId: currentEntity.entityId }
          );
          moveTween.once(Phaser.Tweens.Events.TWEEN_COMPLETE, () => { sprite.playAnimation('idle'); resolve(); });
          moveTween.once(Phaser.Tweens.Events.TWEEN_STOP, () => { sprite.playAnimation('idle'); resolve(); });
        }),
        networkCall: () => combatService.move(this._context.combat.id, tilePos),
        onSuccess: () => {},
        onError: (e) => {
          console.error('[CombatScene] Move failed', e);
        },
        rollback: () => {
          moveTween?.stop();
          sprite.x = startWorld.x;
          sprite.y = startWorld.y;
          sprite.updateOverlayPositions();
          sprite.playAnimation('idle');
        },
      });

      if (result) {
        await this._turnProcessor.enqueueAndProcessResponse(result);
      }
    } finally {
      this._entityManager.removePredictiveMoveEntity(currentEntity.entityId);
      sprite.playAnimation('idle');
      useCombatStore.getState().setQueueBusy(false);
      this._highlights.upateHighlights();
    }
  }

  private isAoeAbility(abilityId: string): boolean {
    const ability = abilityService.getAbilityFromCache(abilityId);
    return ability?.target.aoeRadius != null;
  }

  private async handleTargetClick(tilePos: CombatPosition) {
    const combatStore = useCombatStore.getState();
    const abilityId = combatStore.selectedAbilityId;
    if (!abilityId) return;

    const casterEntity = this._context.getCurrentTurnEntity();
    if (!casterEntity) return;

    // A cast started from a consumable targets identically; only the endpoint differs.
    const itemInstanceId = combatStore.selectedItemInstanceId ?? undefined;

    if (this.isAoeAbility(abilityId)) {
      await this.handleAoeTargetClick(abilityId, casterEntity, tilePos, itemInstanceId);
    } else {
      await this.handleSingleTargetClick(abilityId, casterEntity, tilePos, itemInstanceId);
    }
  }

  /**
   * Sends the confirmed cast — through the item endpoint when a consumable started it,
   * spending the unit from the local bag the way the popover's immediate uses do, since
   * a combat action response carries no inventory.
   */
  private castRequest(
    abilityId: string,
    itemInstanceId: string | undefined,
    targetEntityId?: string,
    targetPosition?: CombatPosition,
  ): Promise<CombatActionResponseDTO> {
    const combatId = this._context.combat.id;
    if (!itemInstanceId) {
      return combatService.useAbility(combatId, abilityId, targetEntityId, targetPosition);
    }
    return combatService.useItem(combatId, itemInstanceId, targetEntityId, targetPosition)
      .then((response) => {
        spendInventoryUnit(itemInstanceId);
        return response;
      });
  }

  private async handleAoeTargetClick(
    abilityId: string,
    casterEntity: EntityCombatStateDTO,
    tilePos: CombatPosition,
    itemInstanceId?: string,
  ) {
    const combatStore = useCombatStore.getState();
    const ability = abilityService.getAbilityFromCache(abilityId);
    if (!ability) return;

    const dist = this._context.battlefield.distanceBetween(casterEntity.position, tilePos);
    if (dist > ability.target.range) {
      combatStore.cancelAbilitySelection();
      return;
    }

    combatStore.cancelAbilitySelection();
    useCombatStore.getState().setQueueBusy(true);
    this._highlights.upateHighlights();
    this._highlights.updateHoverHighlight(null, true);
    try {
      let actionSucceeded = false;
      const result = await runPredictiveAction({
        startAnimation: () => this._turnProcessor.runAbilityStartAnimation(
          abilityId,
          casterEntity.entityId,
          undefined,
          casterEntity.position,
          tilePos,
        ),
        networkCall: () => this.castRequest(abilityId, itemInstanceId, undefined, tilePos),
        onSuccess: () => { actionSucceeded = true; },
        onError: (e) => { console.error('[CombatScene] AoE ability failed', e); },
      });

      if (actionSucceeded && result) {
        await this._turnProcessor.enqueueAndProcessResponse(result, {
          skipAbilityStart: {
            abilityId,
            casterId: casterEntity.entityId,
            // no targetId — skips ALL start events for this cast
          },
        });
      }
    } finally {
      useCombatStore.getState().setQueueBusy(false);
      this._highlights.upateHighlights();
    }
  }

  private async handleSingleTargetClick(
    abilityId: string,
    casterEntity: EntityCombatStateDTO,
    tilePos: CombatPosition,
    itemInstanceId?: string,
  ) {
    const combatStore = useCombatStore.getState();
    const targetEntity = this._context.combat.entityStates.find(
      e => e.position.x === tilePos.x && e.position.y === tilePos.y && e.status !== EntityStatus.DEAD
    );

    if (!targetEntity || !this._context.isValidAbilityTarget(targetEntity)) {
      combatStore.cancelAbilitySelection();
      return;
    }

    combatStore.cancelAbilitySelection();
    useCombatStore.getState().setQueueBusy(true);
    this._highlights.upateHighlights();
    this._highlights.updateHoverHighlight(null, true);
    try {
      let actionSucceeded = false;
      const result = await runPredictiveAction({
        startAnimation: () => this._turnProcessor.runAbilityStartAnimation(
          abilityId,
          casterEntity.entityId,
          targetEntity.entityId,
          casterEntity.position,
          targetEntity.position,
        ),
        networkCall: () => this.castRequest(abilityId, itemInstanceId, targetEntity.entityId),
        onSuccess: () => { actionSucceeded = true; },
        onError: (e) => { console.error('[CombatScene] Use ability failed', e); },
      });

      if (actionSucceeded && result) {
        await this._turnProcessor.enqueueAndProcessResponse(result, {
          skipAbilityStart: {
            abilityId,
            casterId: casterEntity.entityId,
          },
        });
      }
    } finally {
      useCombatStore.getState().setQueueBusy(false);
      this._highlights.upateHighlights();
    }
  }

  // --- Data loading ---

  private async loadCombat(): Promise<[CombatStateDTO, Battlefield]> {
    if (this.gameState?.activeCombat) {
      const combat = this.gameState.activeCombat;
      const battlefield = new Battlefield(combat, this._battlefieldDef);
      return [combat, battlefield];
    }
    throw new Error('No active combat present on game state');
  }

  private async hydrateRoster(combat: CombatStateDTO): Promise<HydratedRoster> {
    const roster: HydratedRoster = {};

    const player = this.gameState.character;
    if (player) {
      roster[player.id] = player as BaseEntityDTO;
    }

    const uniqueArchetypes = new Map<string, BaseEntityDTO>();

    for (const entityState of combat.entityStates) {
      if (roster[entityState.entityId]) continue;

      if (entityState.entityType === EntityType.NPC) {
        let npc = useNpcStore.getState().npcs[entityState.entityId];
        if (!npc) {
          try {
            npc = await npcService.getNpcById(entityState.entityId);
            useNpcStore.getState().setNpc(npc);
          } catch (e) {
            console.warn(`[CombatScene] Failed to fetch NPC ${entityState.entityId}`, e);
          }
        }
        if (npc) {
          let spriteHash = npc.spriteHash;
          if (!spriteHash && npc.spriteType === 'composite' && npc.characterDefinition) {
            spriteHash = await hashDefinition(npc.characterDefinition);
          }
          roster[entityState.entityId] = {
            id: npc.id,
            name: npc.name,
            level: npc.level,
            attributes: npc.attributes ?? {},
            skills: npc.skills ?? {},
            armor: npc.armor,
            sprite: npc.sprite,
            spriteHash,
          };
        }
        continue;
      }

      if (entityState.archetypeId) {
        if (!uniqueArchetypes.has(entityState.archetypeId)) {
          try {
            const enemy = await enemyService.getEnemy(entityState.archetypeId);
            uniqueArchetypes.set(entityState.archetypeId, enemy);
          } catch (e) {
            console.warn(`[CombatScene] Failed to fetch enemy archetype ${entityState.archetypeId}`, e);
          }
        }
        const base = uniqueArchetypes.get(entityState.archetypeId);
        if (base) {
          const enemyState = combat.enemyStates.find(e => e.entityId === entityState.entityId);
          roster[entityState.entityId] = enemyState?.sprite
            ? { ...base, sprite: enemyState.sprite }
            : base;
        }
      }
    }

    return roster;
  }

  private async loadRosterSprites(roster: HydratedRoster): Promise<void> {
    // Map sprite filename → spriteHash (last entity with that sprite wins; same hash for same file)
    const sprites = new Map<string, string | undefined>();
    for (const entity of Object.values(roster)) {
      if (entity.sprite) {
        sprites.set(entity.sprite, entity.spriteHash);
      }
    }

    let queued = 0;

    for (const [sprite] of sprites) {
      const textureKey = getSpriteAssetKey(sprite);
      if (this.textures.exists(textureKey)) continue;

      const url = getSpriteUrl(sprite);
      const type = getSpriteAssetType(sprite);

      if (type === 'zip') {
        this.load.spriteZip(textureKey, url);
        queued++;
      } else if (type === 'png') {
        this.load.image(textureKey, url);
        queued++;
      } else {
        console.warn(`[CombatScene] Unsupported sprite asset: ${sprite}`);
      }
    }

    if (queued > 0) {
      await new Promise<void>((resolve) => {
        this.load.once('complete', () => resolve());
        this.load.start();
      });
    }

    // Load composite sprites (spriteHash but no sprite zip) — each entity gets its own texture key.
    for (const [entityId, entity] of Object.entries(roster)) {
      if (!entity.sprite && entity.spriteHash) {
        const key = `composite-${entityId}`;
        if (!this.textures.exists(key)) {
          await loadCharacterSprite(this, key, entity.spriteHash, entityId);
        }
      }
    }
  }

  // --- Scene setup ---

  update() {
    if (!this._turnProcessor) return;
    this._entityManager.updateDepths(this.getIsoDepth.bind(this));
    this.updateSpriteLayerAlpha();
    if (!this._turnProcessor.queueActive) {
      this.syncFromStore();
    }
  }

  private updateSpriteLayerAlpha(): void {
    if (this._upperTileSprites.length === 0) return;

    const entityData = [...this._entityManager.sprites.values()].map(s => ({
      bounds: s.sprite.getBounds(),
      depth: s.sprite.depth,
    }));

    for (const tileSprite of this._upperTileSprites) {
      const tileBounds = tileSprite.getBounds();
      const tileDepth = tileSprite.depth;
      const entityBehind = entityData.some(
        e => Phaser.Geom.Rectangle.Overlaps(tileBounds, e.bounds) && e.depth < tileDepth
      );
      tileSprite.setAlpha(entityBehind ? SPRITE_LAYER_OVERLAP_ALPHA : 1);
    }
  }

  private createSceneObjects(combat: CombatStateDTO, battlefield: Battlefield, roster: HydratedRoster) {
    if (this._tilemap) {
      this._tilemap.destroy();
    }
    this.children.removeAll();
    for (const s of this._upperTileSprites) s.destroy();
    this._upperTileSprites = [];

    // Phaser doesn't resolve external tileset references automatically.
    // Patch the cached map data to inline each referenced tileset before creating the tilemap.
    // Image texture keys match each tileset's own `name` field, so addTilesetImage(name, name) works uniformly.
    // Build a filename→key map from the def so we look up the correct cache key (TSJ name, not raw filename).
    type TilesetEntry = { source?: string; name?: string; firstgid: number; tileheight?: number; tilewidth?: number; tilecount?: number; columns?: number; margin?: number; spacing?: number; tileoffset?: { x: number; y: number } };
    const tilesetKeyByFilename = new Map(
      this._battlefieldDef.clientAssets.tilesets.map(ts => [
        ts.url.split('/').pop()!.replace('.tsj', ''),
        ts.key,
      ])
    );
    const mapCacheEntry = this.cache.tilemap.get('battlefield-map') as { data: { tilesets: TilesetEntry[] } };
    mapCacheEntry.data.tilesets = mapCacheEntry.data.tilesets.map(ts => {
      if (!ts.source) return ts;
      const filename = ts.source.split('/').pop()!.replace('.tsj', '');
      const key = tilesetKeyByFilename.get(filename);
      if (!key) return ts;
      return { ...this.cache.json.get(`tileset-${key}`) as object, firstgid: ts.firstgid };
    });

    const map = this.make.tilemap({ key: 'battlefield-map' });
    const tilesets = mapCacheEntry.data.tilesets
      .map(ts => map.addTilesetImage(ts.name!, ts.name!))
      .filter((ts): ts is Phaser.Tilemaps.Tileset => ts !== null && !ts.name.includes('sprites'));

    if (tilesets.length === 0) {
      throw new Error('Failed to load battlefield tilesets.');
    }

    // Add per-tile frames to each sprites tileset texture
    for (const ts of mapCacheEntry.data.tilesets) {
      if (!ts.name || !ts.name.includes("sprites")) continue;
      const texture = this.textures.get(ts.name);
      const cols = ts.columns ?? 1;
      const tw = ts.tilewidth ?? map.tileWidth;
      const th = ts.tileheight ?? map.tileHeight;
      const margin = ts.margin ?? 0;
      const spacing = ts.spacing ?? 0;
      for (let i = 0; i < (ts.tilecount ?? 0); i++) {
        const col = i % cols;
        const row = Math.floor(i / cols);
        const x = margin + col * (tw + spacing);
        const y = margin + row * (th + spacing);
        texture.add(i, 0, x, y, tw, th);
      }
    }

    // Create standard tile layers; collect "sprites" layer data for depth-sorted sprite rendering instead.
    const spriteLayerData = map.getLayer('Sprites');
    for (const layerData of map.layers) {
      if (layerData.name === 'Sprites') continue;
      const layer = map.createLayer(layerData.name, tilesets);
      if (!layer) {
        throw new Error(`Failed to create battlefield layer: ${layerData.name}`);
      }
    }

    this._tilemap = map;
    this.initSubModules(combat, battlefield, roster);
    console.log('[CombatScene] Tilemap layer ready', {
      mapSize: { width: map.width, height: map.height },
      tileSize: { width: map.tileWidth, height: map.tileHeight }
    });

    // Render sprites layer tiles as depth-sorted sprites so they occlude entities correctly.
    // Tile image position uses Phaser's isometric formula + tileoffset (same as the tilemap renderer).
    // Depth uses _coords.tileToWorld so Upper tiles sort in the same space as entity sprites.
    if (spriteLayerData) {
      const refTileHeight = tilesets[0].tileHeight;

      const spriteTileSets: TilesetEntry[] = mapCacheEntry.data.tilesets.filter(ts => ts.name && ts.name.includes("sprites"));
      for (let ty = 0; ty < spriteLayerData.height; ty++) {
        for (let tx = 0; tx < spriteLayerData.width; tx++) {
          const tile = spriteLayerData.data[ty]?.[tx];
          if (!tile || tile.index <= 0) continue;

          const phTileset = [...spriteTileSets].reverse().find(ts => ts.firstgid <= tile.index);
          if (!phTileset || !phTileset.name) continue;

          const localId = tile.index - phTileset.firstgid;
          const worldPos = this._coords.tileToWorld(tx + 1, ty + 1);
          const depthPos = this._coords.tileToWorld(tx + 0.5, ty + 0.5);

          const phTileheight = (phTileset?.tileheight ?? this._tilemap.tileHeight);
          const yOffset = phTileheight - refTileHeight - (phTileset.tileoffset?.y ?? 0);

          const img = this.add.image(worldPos.x, worldPos.y + yOffset, phTileset.name, localId);
          img.setOrigin(0.5, 1);
          img.setDepth(this.getIsoDepth(depthPos));
          this._upperTileSprites.push(img);
        }
      }
    }
  }

  private saveCameraState() {
    const combatId = this.gameState.activeCombat?.id;
    if (!combatId) return;
    const camera = this.cameras.main;
    localStorage.setItem(CombatScene.CAMERA_STATE_KEY, JSON.stringify({
      combatId,
      zoom: camera.zoom,
      scrollX: camera.scrollX,
      scrollY: camera.scrollY,
    }));
  }

  private loadSavedCameraState(): { zoom: number; scrollX: number; scrollY: number } | undefined {
    const combatId = this.gameState.activeCombat?.id;
    if (!combatId) return undefined;
    try {
      const raw = localStorage.getItem(CombatScene.CAMERA_STATE_KEY);
      if (!raw) return undefined;
      const state = JSON.parse(raw);
      if (state.combatId !== combatId) return undefined;
      return { zoom: state.zoom, scrollX: state.scrollX, scrollY: state.scrollY };
    } catch {
      return undefined;
    }
  }

  private layoutScene() {
    if (!this._tilemap) return;

    // We add 0.5 here for just a little extra buffer so we zoom out a little extra and get
    // some padding around the edges of the map.
    const maxX = this._tilemap.width + 0.5;
    const maxY = this._tilemap.height + 0.5;
    const cornerPoints = [
      this._coords.tileToWorld(0, 0),
      this._coords.tileToWorld(maxX, 0),
      this._coords.tileToWorld(0, maxY),
      this._coords.tileToWorld(maxX, maxY),
    ];

    const minWorldX = Math.min(...cornerPoints.map(point => point.x));
    const maxWorldX = Math.max(...cornerPoints.map(point => point.x));
    const minWorldY = Math.min(...cornerPoints.map(point => point.y));
    const maxWorldY = Math.max(...cornerPoints.map(point => point.y));
    const mapWidth = maxWorldX - minWorldX;
    const mapHeight = maxWorldY - minWorldY;

    const camera = this.cameras.main;

    const viewportWidth = this.scale.width;
    const viewportHeight = this.scale.height - COMBAT_ACTION_BAR_HEIGHT;

    camera.setViewport(0, 0, viewportWidth, viewportHeight);

    const fitZoom = Math.min(viewportWidth / mapWidth, viewportHeight / mapHeight);
    this._minZoom = fitZoom;
    const maxZoom = this._minZoom * CombatScene.MAX_ZOOM_MULTIPLIER;

    const mapCenterX = minWorldX + mapWidth / 2;
    const mapCenterY = minWorldY + mapHeight / 2;

    // Expand camera bounds so the map can be centered when the viewport is larger
    // than the map at minimum zoom (otherwise Phaser pins to the top-left).
    const visibleWidthAtMinZoom = viewportWidth / this._minZoom;
    const visibleHeightAtMinZoom = viewportHeight / this._minZoom;
    const boundsWidth = Math.max(mapWidth, visibleWidthAtMinZoom);
    const boundsHeight = Math.max(mapHeight, visibleHeightAtMinZoom);
    const boundsX = mapCenterX - boundsWidth / 2;
    const boundsY = mapCenterY - boundsHeight / 2;
    camera.setBounds(boundsX, boundsY, boundsWidth, boundsHeight);

    if (this._isFirstLayout) {
      this._isFirstLayout = false;
      const saved = this.loadSavedCameraState();
      if (saved) {
        // Page refresh while in combat — restore last known zoom/scroll
        camera.setZoom(Phaser.Math.Clamp(saved.zoom, this._minZoom, maxZoom));
        camera.setScroll(saved.scrollX, saved.scrollY);
      } else {
        // Fresh transition — fully zoom out and center
        camera.setZoom(this._minZoom);
        camera.centerOn(mapCenterX, mapCenterY);
      }
    } else {
      // Window resize — clamp zoom but preserve scroll position
      camera.setZoom(Phaser.Math.Clamp(camera.zoom, this._minZoom, maxZoom));
      if (camera.zoom <= this._minZoom) {
        camera.centerOn(mapCenterX, mapCenterY);
      }
    }
  }

  // --- Store sync ---

  private syncFromStore() {
    this._context.updateFromGameStore();
    this._entityManager.sync(this, this._coords, this._context);
    this._highlights.upateHighlights();
  }

  private getIsoDepth(worldPos: { x: number; y: number }): number {
    // Sort by screen Y, then X as a stable tie-breaker for isometric projection.
    return DepthOrder.SPRITES + worldPos.y - worldPos.x * 0.001;
  }
}
